import Formatters from '@/shared/formatters/formatters';
import i18n from '@/plugins/i18n';
import {
  asNumberOrNull,
  asStringOrNull,
  firstValue,
} from '@/shared/utils/type-utils';
import {
  ClassificationJustificationItem,
  ClassificationLevel,
  CorrectClassificationRulePayload,
  ProcedureAppliedRule,
  ProcedureReportFilter,
  ProcedureReportHistoryChange,
  ProcedureReportHistoryItem,
  ProcedureReportHistoryRevision,
  ProcedureReportItem,
  ProcedureReportModel,
  ProcedureReportOption,
  ProcedureReportPagination,
  ProcedureReportStatus,
} from './procedure-report-types';

export const defaultFilter = (): ProcedureReportFilter => ({
  carrierId: [],
  procedureCodeId: '',
  procedureDescriptionId: '',
  tussCodeId: '',
  eventId: [],
  complementId: [],
  groupId: [],
  subGroupId: [],
  predictiveId: [],
  predictiveClassificationId: [],
  status: ['NAO_CLASSIFICADO'],
});

export const defaultPagination = (): ProcedureReportPagination => ({
  page: 1,
  itemsPerPage: 30,
  totalItems: 0,
});

export const initState: ProcedureReportModel = {
  filter: defaultFilter(),
  pagination: defaultPagination(),
  sortBy: 'id',
  sortDesc: false,
  clickAway: false,
  loadingTable: false,
  loadingTableAppend: false,
  loadingExport: false,
  loadingUpdate: false,
  loadingClassification: false,
  loadingRecordHistories: false,
  loadingApproval: false,
  loadingCorrectRule: false,
  records: [],
  recordHistories: [],
  procedureReportsSelecteds: [],
  classification: {
    classified: { counter: 0, percent: 0, description: '' },
    nonClassified: { counter: 0, percent: 0, description: '' },
    pendingApproval: { counter: 0, percent: 0, description: '' },
  },
  showSnackbarCustomize: { show: false, type: null, message: '', top: null },
  showHistoryModal: { show: false },
  justificationModal: { show: false, record: null, editingRule: null },
  carrierOptions: [],
  procedureCodeOptions: [],
  procedureDescriptionOptions: [],
  tussCodeOptions: [],
  eventOptions: [],
  complementOptions: [],
  groupOptions: [],
  subGroupOptions: [],
  predictiveOptions: [],
  predictiveClassificationOptions: [],
};

const formatter = new Formatters();
const translate = (key: string) => String(i18n.t(key));

export const buildFilterParams = (filter: ProcedureReportFilter): Record<string, any> => {
  const params: Record<string, any> = {};

  if (filter.carrierId.length) params.carrierIds = filter.carrierId;
  if (filter.procedureCodeId) params.procedureCode = filter.procedureCodeId;
  if (filter.procedureDescriptionId) params.procedureDescription = filter.procedureDescriptionId;
  if (filter.tussCodeId) params.tussCode = filter.tussCodeId;
  if (filter.eventId.length) params.eventIds = filter.eventId;
  if (filter.complementId.length) params.complementIds = filter.complementId;
  if (filter.groupId.length) params.groupIds = filter.groupId;
  if (filter.subGroupId.length) params.subgroupIds = filter.subGroupId;
  if (filter.predictiveId.length) params.predictiveIds = filter.predictiveId;
  if (filter.predictiveClassificationId.length) params.predictiveClassificationIds = filter.predictiveClassificationId;
  if (filter.status.length) params.statuses = filter.status;

  return params;
};

const normalizeClassificationOrigin = (
  value: unknown,
): ProcedureReportItem['classificationOrigin'] => {
  const normalized = asStringOrNull(value);
  return normalized === 'IA' || normalized === 'MANUAL' ? normalized : null;
};

const contentFromPayload = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  return [];
};

export const normalizePage = (payload: any) => {
  const pagePayload = payload?.data ?? payload ?? {};
  const content = contentFromPayload(pagePayload);
  const totalElements = Number(
    pagePayload.totalElements
      ?? pagePayload.total
      ?? pagePayload.data?.totalElements
      ?? content.length,
  );

  return {
    content,
    totalElements: Number.isNaN(totalElements) ? content.length : totalElements,
    last: Boolean(pagePayload.last ?? pagePayload.data?.last ?? content.length === 0),
  };
};

export const normalizeOption = (
  item: any,
  valueKeys: string[] = ['id', 'code'],
  textKeys: string[] = ['name', 'description', 'code'],
): ProcedureReportOption | null => {
  const idRaw = firstValue(item, valueKeys);
  const nameRaw = firstValue(item, textKeys) ?? idRaw;

  if (idRaw === null || idRaw === undefined || idRaw === '') return null;

  const id: string | number = typeof idRaw === 'number' || typeof idRaw === 'string'
    ? idRaw
    : String(idRaw);

  return {
    id,
    name: String(nameRaw),
  };
};

export const normalizeOptionsPage = (
  payload: any,
  valueKeys?: string[],
  textKeys?: string[],
) => {
  const page = normalizePage(payload);
  const seen = new Set<string>();
  const content = page.content
    .map((item) => normalizeOption(item, valueKeys, textKeys))
    .filter((item): item is ProcedureReportOption => {
      if (!item) return false;
      const key = String(item.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return {
    ...page,
    content,
  };
};

const CLASSIFICATION_LEVELS: ClassificationLevel[] = [
  'EVENT',
  'COMPLEMENT',
  'GROUP',
  'SUBGROUP',
  'PREDICTIVE',
  'PREDICTIVE_CLASSIFICATION',
];

const isClassificationLevel = (value: string): value is ClassificationLevel => (
  CLASSIFICATION_LEVELS.includes(value as ClassificationLevel)
);

const serializeClassificationJustification = (raw: unknown): string | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (Array.isArray(raw)) return JSON.stringify(raw);
  return asStringOrNull(raw);
};

const normalizeJustificationLevel = (raw: string): ClassificationLevel | null => {
  const normalized = String(raw ?? '').trim().toUpperCase();
  const aliases: Record<string, ClassificationLevel> = {
    GRUPO: 'GROUP',
    SUBGRUPO: 'SUBGROUP',
  };
  const level = (aliases[normalized] ?? normalized) as ClassificationLevel;
  return isClassificationLevel(level) ? level : null;
};

export const parseClassificationJustification = (
  raw: unknown,
): ClassificationJustificationItem[] => {
  let parsed: unknown = raw;

  if (raw === null || raw === undefined || raw === '') return [];

  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((item: any): ClassificationJustificationItem | null => {
      const level = normalizeJustificationLevel(item?.level);
      if (!level) return null;

      return {
        level,
        classificacao: String(item?.classificacao ?? ''),
        dominioOk: item?.dominioOk === true,
        regraId: Number(item?.regraId ?? 0),
        motivo: String(item?.motivo ?? ''),
      };
    })
    .filter((item): item is ClassificationJustificationItem => Boolean(item));
};

const normalizeAppliedRules = (rules: unknown): ProcedureAppliedRule[] => {
  if (!Array.isArray(rules)) return [];

  return rules
    .map((rule: any): ProcedureAppliedRule | null => {
      const level = normalizeJustificationLevel(rule?.level);
      if (!level) return null;

      return {
        ruleId: Number(rule?.ruleId ?? rule?.id ?? 0),
        level,
        value: String(rule?.value ?? ''),
        criteria: asStringOrNull(rule?.criteria),
      };
    })
    .filter((rule): rule is ProcedureAppliedRule => Boolean(rule));
};

const mapJustificationItemsToRules = (
  items: ClassificationJustificationItem[],
): ProcedureAppliedRule[] => (
  items.map((item) => ({
    ruleId: item.regraId,
    level: item.level,
    value: item.classificacao,
    reason: item.motivo || null,
    dominioOk: item.dominioOk,
  }))
);

const buildCriteriaByLevel = (
  appliedRules: ProcedureAppliedRule[] | undefined,
): Partial<Record<ClassificationLevel, string>> => (
  (appliedRules ?? []).reduce((criteriaByLevel, rule) => {
    if (rule.criteria) {
      criteriaByLevel[rule.level] = rule.criteria;
    }
    return criteriaByLevel;
  }, {} as Partial<Record<ClassificationLevel, string>>)
);

export const getClassificationRules = (
  record: Pick<ProcedureReportItem, 'classificationJustification' | 'appliedRules'> | null | undefined,
): ProcedureAppliedRule[] => {
  const criteriaByLevel = buildCriteriaByLevel(record?.appliedRules);
  const rulesFromJustification = mapJustificationItemsToRules(
    parseClassificationJustification(record?.classificationJustification),
  );

  if (rulesFromJustification.length > 0) {
    return rulesFromJustification.map((rule) => ({
      ...rule,
      criteria: criteriaByLevel[rule.level] ?? null,
    }));
  }

  return record?.appliedRules ?? [];
};

export const hasClassificationJustification = (
  record: Pick<ProcedureReportItem, 'classificationJustification' | 'appliedRules'> | null | undefined,
): boolean => (
  parseClassificationJustification(record?.classificationJustification).length > 0
  || (record?.appliedRules?.length ?? 0) > 0
);

export const normalizeProcedureStatus = (value: unknown): ProcedureReportStatus | null => {
  const raw = asStringOrNull(value);
  if (!raw) return null;

  const normalized = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (normalized === 'PENDENTE_APROVACAO' || normalized === 'PENDENTE_DE_APROVACAO') {
    return 'PENDENTE_APROVACAO';
  }
  if (normalized === 'CLASSIFICADO') return 'CLASSIFICADO';
  if (normalized === 'NAO_CLASSIFICADO') return 'NAO_CLASSIFICADO';

  return normalized as ProcedureReportStatus;
};

export const normalizeProcedure = (row: any): ProcedureReportItem => {
  const rawJustification = firstValue(row, ['classificationJustification', 'reasonAi']);
  const idRaw = firstValue(row, ['id', 'dimProcedureId', 'procedureId']);
  const id: string | number = typeof idRaw === 'number' || typeof idRaw === 'string'
    ? idRaw
    : `procedure-${Date.now()}`;

  return {
    id,
    carrierId: asNumberOrNull(firstValue(row, ['carrierId', 'carrier.id', 'operatorId', 'operator.id'])),
    procedureCodeId: asStringOrNull(firstValue(row, ['procedureCodeId', 'procedureCode', 'code'])),
    procedureDescriptionId: asStringOrNull(firstValue(row, ['procedureDescriptionId', 'procedureDescription', 'description'])),
    tussCodeId: asStringOrNull(firstValue(row, ['tussCodeId', 'tussCode', 'tuss'])),
    eventId: asNumberOrNull(firstValue(row, ['eventId', 'event.id'])),
    groupId: asNumberOrNull(firstValue(row, ['groupId', 'group.id'])),
    subGroupId: asNumberOrNull(firstValue(row, ['subGroupId', 'subgroupId', 'subGroup.id', 'subgroup.id'])),
    complementId: asNumberOrNull(firstValue(row, ['complementId', 'complement.id'])),
    predictiveId: asNumberOrNull(firstValue(row, ['predictiveId', 'predictive.id'])),
    predictiveClassificationId: asNumberOrNull(firstValue(row, ['predictiveClassificationId', 'predictiveClassification.id'])),
    additionalTussCode: asStringOrNull(firstValue(row, ['additionalTussCode'])),
    updateDescription: Boolean(firstValue(row, ['updateDescription'])),
    active: firstValue(row, ['active']) !== false,
    status: normalizeProcedureStatus(firstValue(row, ['status', 'classificationStatus'])),
    classificationOrigin: normalizeClassificationOrigin(firstValue(row, ['classificationOrigin'])),
    classificationJustification: serializeClassificationJustification(rawJustification),
    appliedRules: normalizeAppliedRules(row?.appliedRules),
    editedBy: asStringOrNull(firstValue(row, ['editedBy', 'updatedBy', 'lastModifiedBy'])),
    editedAt: asStringOrNull(firstValue(row, ['editedAt', 'updatedAt', 'lastModifiedAt'])),
  };
};

export const normalizeProceduresPage = (payload: any) => {
  const page = normalizePage(payload);

  return {
    ...page,
    content: page.content.map((row) => normalizeProcedure(row)),
  };
};

export const normalizeProcedureOptions = (payload: any) => {
  const rows = contentFromPayload(payload?.data ?? payload);
  const mappings = {
    carrierOptions: {
      valueKeys: ['carrierId', 'carrier.id'],
      textKeys: ['carrierDescription', 'carrier.name', 'carrier.description'],
    },
    procedureCodeOptions: {
      valueKeys: ['procedureCode', 'procedureCodeId', 'code'],
      textKeys: ['procedureCode', 'procedureCodeId', 'code'],
    },
    procedureDescriptionOptions: {
      valueKeys: ['procedureDescription', 'procedureDescriptionId', 'description'],
      textKeys: ['procedureDescription', 'procedureDescriptionId', 'description'],
    },
    tussCodeOptions: {
      valueKeys: ['tussCode', 'tussCodeId', 'tuss'],
      textKeys: ['tussCode', 'tussCodeId', 'tuss'],
    },
    eventOptions: {
      valueKeys: ['event.id', 'eventId'],
      textKeys: ['event.description', 'event.name', 'eventDescription'],
    },
    complementOptions: {
      valueKeys: ['complement.id', 'complementId'],
      textKeys: ['complement.description', 'complement.name', 'complementDescription'],
    },
    groupOptions: {
      valueKeys: ['group.id', 'groupId'],
      textKeys: ['group.description', 'group.name', 'groupDescription'],
    },
    subGroupOptions: {
      valueKeys: ['subgroup.id', 'subGroup.id', 'subgroupId', 'subGroupId'],
      textKeys: [
        'subgroup.description',
        'subGroup.description',
        'subgroup.name',
        'subGroup.name',
        'subgroupDescription',
        'subGroupDescription',
      ],
    },
    predictiveOptions: {
      valueKeys: ['predictive.id', 'predictiveId'],
      textKeys: ['predictive.description', 'predictive.name', 'predictiveDescription'],
    },
    predictiveClassificationOptions: {
      valueKeys: ['predictiveClassification.id', 'predictiveClassificationId'],
      textKeys: [
        'predictiveClassification.description',
        'predictiveClassification.name',
        'predictiveClassificationDescription',
      ],
    },
  };

  return Object.entries(mappings).reduce((options, [key, mapping]) => ({
    ...options,
    [key]: rows
      .map((row) => normalizeOption(row, mapping.valueKeys, mapping.textKeys))
      .filter((option): option is ProcedureReportOption => Boolean(option)),
  }), {} as Record<string, ProcedureReportOption[]>);
};

export const buildTussCode = (procedureCode: string | null): string | null => {
  if (!procedureCode) return null;
  return `${procedureCode}0`.padStart(12, '0').slice(-12);
};

export const buildProcedurePayload = (record: ProcedureReportItem): Record<string, any> => ({
  ...(typeof record.id === 'number' ? { id: record.id } : {}),
  procedureCode: record.procedureCodeId,
  carrierId: record.carrierId,
  procedureDescription: record.procedureDescriptionId,
  tussCode: record.tussCodeId,
  additionalTussCode: record.additionalTussCode,
  updateDescription: record.updateDescription,
  active: record.active,
  status: record.status,
  eventId: record.eventId,
  groupId: record.groupId,
  subgroupId: record.subGroupId,
  complementId: record.complementId,
  predictiveId: record.predictiveId,
  predictiveClassificationId: record.predictiveClassificationId,
});

export const buildBatchPayload = (
  records: ProcedureReportItem[],
  form: Partial<ProcedureReportItem>,
): Record<string, any>[] => {
  const allowedFields: { formField: keyof ProcedureReportItem; payloadField: string }[] = [
    { formField: 'eventId', payloadField: 'eventId' },
    { formField: 'groupId', payloadField: 'groupId' },
    { formField: 'subGroupId', payloadField: 'subgroupId' },
    { formField: 'complementId', payloadField: 'complementId' },
    { formField: 'predictiveId', payloadField: 'predictiveId' },
    { formField: 'predictiveClassificationId', payloadField: 'predictiveClassificationId' },
  ];

  return records.map((record) => {
    const payload = buildProcedurePayload(record);

    allowedFields.forEach(({ formField, payloadField }) => {
      const value = form[formField];
      if (value !== null && value !== undefined && value !== '') {
        payload[payloadField] = value;
      }
    });

    return { ...payload };
  });
};

export const buildCorrectRulePayload = (
  payload: CorrectClassificationRulePayload,
): Record<string, any> => ({
  procedureId: payload.procedureId,
  level: payload.level,
  value: payload.value,
});

export const mergeCorrectedProcedureRecord = (
  previous: ProcedureReportItem | null | undefined,
  updated: ProcedureReportItem,
  payload: CorrectClassificationRulePayload,
  createdRule?: Record<string, unknown> | null,
): ProcedureReportItem => {
  const justificationRaw = updated.classificationJustification
    ?? previous?.classificationJustification
    ?? null;

  const newRuleId = Number(createdRule?.ruleId ?? createdRule?.id ?? 0);
  const items = parseClassificationJustification(justificationRaw).map((item) => (
    item.level === payload.level
      ? {
        ...item,
        classificacao: payload.value,
        regraId: newRuleId || item.regraId,
      }
      : item
  ));

  const appliedRules = (updated.appliedRules ?? previous?.appliedRules ?? []).map((rule) => (
    rule.level === payload.level
      ? {
        ...rule,
        ruleId: newRuleId || rule.ruleId,
        value: payload.value,
      }
      : rule
  ));

  return {
    ...(previous ?? {}),
    ...updated,
    classificationOrigin: updated.classificationOrigin ?? 'MANUAL',
    classificationJustification: items.length > 0
      ? serializeClassificationJustification(items)
      : justificationRaw,
    appliedRules,
  };
};

export const normalizeApproveResponse = (
  response: any,
  requestedIds: number[],
) => {
  const payload = response?.data ?? response ?? null;

  if (Array.isArray(payload)) {
    const approvedRecords = payload.map((row) => normalizeProcedure(row));
    const approvedIds = approvedRecords
      .map((row) => Number(row.id))
      .filter((id) => !Number.isNaN(id));

    return {
      approvedRecords,
      approvedCount: approvedRecords.length,
      ignoredIds: requestedIds.filter((id) => !approvedIds.includes(id)),
    };
  }

  if (payload && typeof payload === 'object') {
    const ignoredIds = Array.isArray(payload.ignored)
      ? payload.ignored.map((id: unknown) => Number(id)).filter((id: number) => !Number.isNaN(id))
      : [];

    const approvedRecords = Array.isArray(payload.records)
      ? payload.records.map((row: unknown) => normalizeProcedure(row))
      : [];

    const approvedCount = typeof payload.approved === 'number'
      ? payload.approved
      : requestedIds.filter((id) => !ignoredIds.includes(id)).length;

    return {
      approvedRecords,
      approvedCount,
      ignoredIds,
    };
  }

  return {
    approvedRecords: [],
    approvedCount: 0,
    ignoredIds: [...requestedIds],
  };
};

export const normalizeClassification = (payload: any) => {
  const extractData = (source: any) => {
    const root = source?.data ?? source ?? {};
    if (root.classified || root.nonClassified || root.pendingApproval) return root;

    const nested = root?.data ?? {};
    if (nested.classified || nested.nonClassified || nested.pendingApproval) return nested;

    return root;
  };

  const normalizePercent = (value: unknown) => {
    const percent = Number(value ?? 0);
    if (Number.isNaN(percent)) return 0;
    return percent;
  };

  const normalizeCounter = (counter: any) => ({
    counter: Number(
      counter?.counter
      ?? counter?.count
      ?? counter?.total
      ?? 0,
    ),
    percent: normalizePercent(counter?.percent ?? counter?.percentage),
    description: String(counter?.description ?? ''),
  });

  const data = extractData(payload);

  return {
    classified: normalizeCounter(
      data.classified ?? data.CLASSIFICADO,
    ),
    nonClassified: normalizeCounter(
      data.nonClassified ?? data.non_classified ?? data.NAO_CLASSIFICADO,
    ),
    pendingApproval: normalizeCounter(
      data.pendingApproval ?? data.pending_approval ?? data.PENDENTE_APROVACAO,
    ),
  };
};

const normalizeHistoryType = (value: unknown): string => {
  switch (value) {
    case 'ADD':
      return translate('admissionMap.history.types.added');
    case 'MOD':
      return translate('admissionMap.history.types.updated');
    case 'DEL':
      return translate('admissionMap.history.types.deleted');
    default:
      return String(value);
  }
};

const normalizeAttributeName = (value: unknown): string => {
  switch (value) {
    case 'carrierName':
    case 'carrierId':
      return translate('procedureReport.fields.carrier');
    case 'procedureCode':
    case 'procedureCodeId':
      return translate('procedureReport.fields.procedureCode');
    case 'procedureDescription':
    case 'procedureDescriptionId':
      return translate('procedureReport.fields.procedureDescription');
    case 'tussCode':
    case 'tussCodeId':
      return translate('procedureReport.fields.tussCode');
    case 'additionalTussCode':
      return translate('procedureReport.fields.additionalTussCode');
    case 'updateDescription':
      return translate('procedureReport.fields.updateDescription');
    case 'active':
      return translate('procedureReport.fields.active');
    case 'status':
      return translate('procedureReport.fields.status');
    case 'eventId':
      return translate('procedureReport.fields.event');
    case 'groupId':
      return translate('procedureReport.fields.group');
    case 'subgroupId':
    case 'subGroupId':
      return translate('procedureReport.fields.subGroup');
    case 'preventiveId':
      return translate('procedureReport.fields.preventive');
    case 'preventiveClassificationId':
      return translate('procedureReport.fields.preventiveClassification');
    case 'complementId':
      return translate('procedureReport.fields.complement');
    case 'predictiveId':
      return translate('procedureReport.fields.predictive');
    case 'predictiveClassificationId':
      return translate('procedureReport.fields.predictiveClassification');
    case 'editedBy':
      return translate('procedureReport.fields.editedBy');
    case 'editedAt':
      return translate('procedureReport.fields.editedAt');
    default:
      return String(value);
  }
};

const normalizeHistoryValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') {
    return value ? translate('admissionMap.boolean.yes') : translate('admissionMap.boolean.no');
  }
  const raw = String(value);
  const lower = raw.toLowerCase();
  if (lower === 'true') return translate('admissionMap.boolean.yes');
  if (lower === 'false') return translate('admissionMap.boolean.no');
  if (raw.toUpperCase() === 'CLASSIFICADO') return translate('procedureReport.filter.status.classified');
  if (raw.toUpperCase() === 'NAO_CLASSIFICADO') return translate('procedureReport.filter.status.unclassified');
  if (raw.toUpperCase() === 'PENDENTE_APROVACAO') return translate('procedureReport.filter.status.pendingApproval');
  return raw;
};

export const normalizeHistory = (payload: any): ProcedureReportHistoryItem[] => {
  const data = payload ?? [];
  try {
    const items: ProcedureReportHistoryItem[] = data.map((row: ProcedureReportHistoryItem) => ({
      carrierName: (row as any).carrierName ?? (row as any).carrier ?? (row as any).carrierId ?? null,
      procedureCode: (row as any).procedureCode ?? (row as any).procedureCodeId ?? null,
      revisions: ((row as any).revisions ?? []).map((rev: ProcedureReportHistoryRevision) => ({
        revType: rev.revType ? normalizeHistoryType(rev.revType) : null,
        timestamp: rev.timestamp ? formatter.formatDateTimeZoneWithHours(rev.timestamp) : null,
        userName: rev.userName ?? rev.userEmail ?? null,
        userEmail: rev.userEmail ?? null,
        changes: (rev.changes ?? []).map((change: ProcedureReportHistoryChange) => ({
          attributeName: change.attributeName ? normalizeAttributeName(change.attributeName) : null,
          oldValue: normalizeHistoryValue(change.oldValue),
          newValue: normalizeHistoryValue(change.newValue),
        })),
      })),
    }));

    return items;
  } catch (error) {
    return [];
  }
};
